import { ArrowLeft, CheckCircle2, Clock, Mail, RefreshCw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactElement, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from '@/i18n/routing';

interface ClaimEmailSentCardProps {
  maskedEmail: string | null | undefined;
  professionalId: string;
  /** Token lifetime in hours, echoed from the backend so the "expires in Xh"
      copy here matches the email the user just received. Single source of
      truth: Settings().CLAIM_TOKEN_EXPIRY_HOURS. */
  expiresHours: number | null;
  /** Re-send the magic link via the parent's claim hook. The parent owns the
      Redux/state machine; this card is presentational + countdown logic. */
  onResend: () => Promise<void>;
  /** Disables the resend button while the parent's request is in flight so
      double-clicks can't queue two emails. */
  isResending: boolean;
}

/** Seconds the user must wait between sending and clicking "Resend". Aligned
    with industry-standard verification flows (Stripe, Clerk, Auth0 use 30-60s)
    and well below the per-IP rate limit (10/h) so it's a UX guardrail, not a
    server-side quota. */
const RESEND_COOLDOWN_SECONDS = 60;

/**
 * Post-initiate success card. Brand-aligned (brand-tertiary + status-info),
 * all-centered layout, a single muted "Xh" pill instead of a wide Alert,
 * a quiet back-to-profile link, plus (#3815 PR3) a resend button with a
 * cooldown countdown and a "wrong email? contact support" escape hatch.
 */
export function ClaimEmailSentCard({
  maskedEmail,
  professionalId,
  expiresHours,
  onResend,
  isResending,
}: ClaimEmailSentCardProps): ReactElement {
  const t = useTranslations('Lawyers.claim');

  // Cooldown ticks down from RESEND_COOLDOWN_SECONDS to 0. Resend is disabled
  // while > 0 and while the parent is mid-request.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldown]);

  async function handleResend(): Promise<void> {
    if (cooldown > 0 || isResending) return;
    try {
      await onResend();
    } finally {
      // Restart the cooldown after the resend completes — both on success
      // (don't let users hammer the endpoint) and on error (typed in the
      // parent's state, the user sees the error; ratchet anyway so the
      // 429-from-IP-limit case can't be repeated immediately). The parent
      // owns error display; we just enforce the time window regardless of
      // outcome so a thrown network error can't bypass rate limiting.
      setCooldown(RESEND_COOLDOWN_SECONDS);
    }
  }

  const resendDisabled = cooldown > 0 || isResending;

  // Dynamic aria-label reflects the actual button state so screen-reader
  // users get the same temporal context as sighted users. A static label
  // ("Resend verification email") hides the cooldown entirely from AT.
  const resendAriaLabel =
    cooldown > 0
      ? t('resendInAriaLabel', { seconds: cooldown })
      : isResending
        ? t('resendingAriaLabel')
        : t('resendEmailAriaLabel');

  return (
    <main className='min-h-[80vh] px-4 py-10 sm:px-6 lg:px-8'>
      <div className='mx-auto w-full max-w-xl'>
        <Card className='overflow-hidden border-line-default bg-surface-subtle shadow-sm'>
          <div className='h-1 bg-gradient-to-r from-brand-tertiary via-status-info to-brand-tertiary' />

          <div className='flex flex-col items-center px-6 py-10 text-center sm:px-10'>
            {/* Icon tile — brand gradient with a small success check overlay
                so the state reads as "success" without leaving the brand. */}
            <div className='relative mb-5'>
              <div className='flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-tertiary/25 to-status-info/25 ring-1 ring-brand-tertiary/40 shadow-sm'>
                <Mail className='h-8 w-8 text-brand-tertiary' />
              </div>
              <span className='absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-surface-subtle ring-2 ring-surface-subtle'>
                <CheckCircle2 className='h-5 w-5 text-status-success' />
              </span>
            </div>

            <h1 className='text-2xl font-semibold tracking-tight text-content-primary'>
              {t('emailSent')}
            </h1>

            {/* Single-line sent-to: address stays on its own font-mono run so
                the masking is legible ("a***@gmail.com"). */}
            <p className='mt-2 text-sm text-content-secondary'>
              {t('emailSentTo', { email: '' })}
              <span className='font-mono text-content-primary'>
                {maskedEmail}
              </span>
            </p>

            <p className='mt-3 max-w-sm text-sm text-content-muted'>
              {t('checkInbox')}
            </p>

            {/* Expiry pill — only shown when the backend supplied the TTL.
                Guards against older responses that didn't include
                expires_hours (forward compat) and keeps the single-source-
                of-truth rule intact (no hardcoded "24"/"72" fallback). */}
            {expiresHours !== null ? (
              <div className='mt-6 inline-flex items-center gap-1.5 rounded-full border border-status-info/20 bg-status-info/10 px-3 py-1.5 text-xs font-medium text-status-info'>
                <Clock className='h-3.5 w-3.5' />
                {t('linkExpires', { hours: expiresHours })}
              </div>
            ) : null}

            {/* Resend block — quiet button below the expiry pill. Cooldown
                pre-empts repeated taps (UX) and gives Redis-INCR a clean
                "you've already done this" boundary so the per-IP limit at
                the next layer isn't burned through by impatient users. */}
            <div className='mt-6 flex flex-col items-center gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={handleResend}
                disabled={resendDisabled}
                aria-label={resendAriaLabel}
              >
                <RefreshCw className='mr-1.5 h-4 w-4' />
                {cooldown > 0
                  ? t('resendIn', { seconds: cooldown })
                  : isResending
                    ? t('resending')
                    : t('resendEmail')}
              </Button>
            </div>

            {/* Subordinate next action — quiet text link, not a competing button. */}
            <Link
              href={{
                pathname: '/advokati/[id]',
                params: { id: professionalId },
              }}
              className='mt-8 inline-flex items-center gap-1.5 text-sm text-content-secondary transition-colors hover:text-brand-tertiary'
            >
              <ArrowLeft className='h-4 w-4' />
              {t('backToProfile')}
            </Link>

            {/* "Wrong email? Contact support" escape hatch (#3815 PR3). For
                lawyers whose SAK registry email is stale or wrong — they
                can't claim with the email shown, so give them a way out
                that doesn't dead-end at "open a ticket on a Slovak help
                desk." Plain mailto: keeps it dependency-free. */}
            <p className='mt-3 text-xs text-content-muted'>
              {t.rich('wrongEmailEscapeHatch', {
                a: (chunks) => (
                  <a
                    href={`mailto:info@lexomat.sk?subject=${encodeURIComponent(t('wrongEmailMailtoSubject'))}`}
                    className='font-medium text-brand-tertiary underline transition-colors hover:text-brand-tertiary-hover'
                  >
                    {chunks}
                  </a>
                ),
              })}
            </p>
          </div>
        </Card>
      </div>
    </main>
  );
}
